import React from "react";
import { useObservable } from "@ngneat/react-rxjs";
import { Box, CardActionArea, Grid, Stack, styled, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Colors } from "@constants/colors.constant";
import { BoostCreditsModel } from "@store/common/boostCredits.model";
import { JobBoardAndSequenceAndBoostActionAndSchoolUI, jobBoardsQuery, jobBoardsService, School } from "@store/jobBoards";
import ImageWithRetryFeature from "@components/ImageWithRetryFeature.component";

interface SchoolCardStyledProps {
  breakpointDownSM?: boolean;
  isSelected?: boolean;
}

const SchoolCardStyled = styled(Stack)(
  (props: SchoolCardStyledProps) => `
  box-shadow: 0px 0px 20px rgba(34, 34, 64, 0.05);
  background-color: ${Colors.white};
  border: 1px solid ${props.isSelected ? Colors.primary : "transparent"};
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  border-radius: 20px;
  height: ${props.breakpointDownSM ? 130 : 158}px;
  width: ${props.breakpointDownSM ? 130 : 158}px;
  margin: auto;
  opacity: 1;
  flex: 1;
  position: relative;
  & > .MuiCardActionArea-root {
    background-color: ${Colors.white};
    background-repeat: no-repeat;
    background-position: center;
    backdrop-filter: blur(20px);
    align-items: flex-start;
    background-size: cover;
    border-radius: 20px;
    padding: 10px;
    height: 100%;
  }
  &:hover {
    & .MuiCardActionArea-focusHighlight {
      background-color: ${Colors.primary};
      opacity: 0.1;
    }
  }
`
);

const CreditBadgeStyled = styled(Stack)`
  background-color: ${Colors.lightPrimaryNoAlpha};
  height: 22px;
  width: fit-content;
  z-index: 1;
  position: absolute;
  top: 10px;
  left: 10px;
  border-radius: 4px;
`;

interface SchoolsListProps {
  boostCredits?: BoostCreditsModel[];
}

const SchoolsList = (props: SchoolsListProps) => {
  const { boostCredits = [] } = props;

  const { t } = useTranslation();

  const theme = useTheme();
  const breakpointDownSM = useMediaQuery(theme.breakpoints.down("sm"));

  const [schools] = useObservable(jobBoardsQuery.schools$);

  const [filters] = useObservable(jobBoardsQuery.filters$);

  const schoolTypes = schools.reduce((types, school) => {
    if (!!filters?.search && !school.name.toLowerCase().includes(filters.search.toLowerCase())) return types;
    const withCredits = boostCredits.find((x) => x.boostItemId === school.id);
    const typeName = withCredits ? t("boostRequest.selection.myself.credits.withCredits") : school.type || t("global.others");
    const type = types[typeName] || [];
    type.push(school);
    return { ...types, [typeName]: type };
  }, {} as { [type: string]: (School & JobBoardAndSequenceAndBoostActionAndSchoolUI)[] });

  return (
    <Stack minHeight="100%" alignItems="center">
      <Stack spacing={4}>
        {Object.keys(schoolTypes)
          .sort((a, b) => {
            if (a === t("boostRequest.selection.myself.credits.withCredits")) return -1;
            if (b === t("boostRequest.selection.myself.credits.withCredits")) return 1;
            return a.localeCompare(b);
          })
          .filter((schoolType) => !!schoolTypes[schoolType].length)
          .map((schoolType, index) => (
            <Stack key={`Jobboard type ${index}`} spacing={2}>
              <Typography fontSize={20} fontWeight="700" textAlign="center" lineHeight={1.25}>
                {schoolType}
              </Typography>
              <Box>
                <Grid container spacing={3} justifyContent={breakpointDownSM ? "center" : undefined}>
                  {schoolTypes[schoolType].map((school) => {
                    const boostCredit = boostCredits.find((x) => x.boostItemId === school.id);
                    const disabledAction = school.price <= 0 || (boostCredit && boostCredit.credits - boostCredit.creditsWaitingForValidation <= 0);

                    return (
                      <Grid item key={school.id}>
                        <SchoolCardStyled isSelected={school.selected} breakpointDownSM={breakpointDownSM}>
                          {boostCredit && (
                            <CreditBadgeStyled alignItems={"center"} justifyContent={"center"}>
                              <Typography mx={"10px"} color={Colors.primary} fontWeight={500} fontSize={11}>
                                {t("boostRequest.selection.myself.credits.amount", {
                                  count: boostCredit.credits - boostCredit.creditsWaitingForValidation,
                                })}
                              </Typography>
                            </CreditBadgeStyled>
                          )}
                          <Stack
                            disableRipple={disabledAction}
                            disableTouchRipple={disabledAction}
                            sx={{
                              cursor: disabledAction ? "default" : undefined,
                              opacity: disabledAction ? 0.35 : 1,
                            }}
                            onClick={() => {
                              if (!disabledAction) jobBoardsService.selectJobBoardOrSequenceOrBoostAction(school.id);
                            }}
                            component={CardActionArea}>
                            <Stack width="100%" height="100%" alignItems="flex-end" position="relative">
                              <Tooltip
                                sx={{ position: "relative" }}
                                title={
                                  <Stack spacing={0.25}>
                                    <Typography fontSize={12} fontWeight="500">
                                      {school.name}
                                    </Typography>
                                    <Typography fontSize={12} fontWeight="300" fontStyle="italic" lineHeight={1.25} whiteSpace="pre-line">
                                      {school.information}
                                    </Typography>
                                  </Stack>
                                }
                                arrow
                                placement="top">
                                <img alt="details" style={{ position: "absolute" }} src="/images/icon_informations.svg" />
                              </Tooltip>
                              {school.picture ? (
                                <ImageWithRetryFeature
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    objectPosition: "center",
                                    alignSelf: "center",
                                    margin: "auto",
                                  }}
                                  src={school.picture}
                                  alt={school.name}
                                />
                              ) : (
                                <Typography
                                  alignSelf="center"
                                  m="auto"
                                  textAlign="center"
                                  sx={{ wordBreak: "break-word" }}
                                  lineHeight={1.25}
                                  fontSize={18}
                                  fontWeight="700"
                                  color={Colors.grey}>
                                  {school.name}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        </SchoolCardStyled>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Stack>
          ))}
      </Stack>
    </Stack>
  );
};

export default SchoolsList;
